/**
 * AI-Tell Detector — pure-JS heuristic scores that flag common AI-image
 * pathologies. Phase 2.B.3.
 *
 * No ML model, no external calls. Three scalar scores in [0, 1]:
 *
 *   bandingScore       — gradient banding in dark midtones / shadows
 *                        (typical of low-step diffusion + 8-bit VAE
 *                        decode). Histogram-gap measure: high score
 *                        when the shadow histogram has periodic spikes
 *                        rather than a smooth distribution.
 *
 *   haloScore          — over-saturated boundary halos (universal
 *                        SDXL/Flux artifact at subject/background
 *                        interfaces). Detected via correlation
 *                        between absolute luminance gradient and
 *                        saturation: real photo edges desaturate at
 *                        high contrast; AI halos saturate.
 *
 *   plasticSkinScore   — flat / over-smoothed faces. Approximate
 *                        flesh-tone region detection (no face model)
 *                        + high-frequency-energy-ratio measure: real
 *                        skin has fine pore detail in mid-frequency,
 *                        AI skin doesn't.
 *
 * The combined `aiLikelihoodScore` is the max of the three (a single
 * strong tell is enough to be suspicious). All scores are advisory —
 * they fire false positives on heavily-stylised real photos.
 *
 * Operates on the editor's RGBA Float32 buffer in linear ACEScg.
 * Doesn't need ImageData; can be called any time after loadImage.
 */

const SHADOW_LUM_MAX = 0.08;       /* "shadows" are luminance < 0.08 in linear */
const FLESH_HUE_RANGE = [10, 50];  /* rough HSV hue band for caucasian/medium flesh, in degrees */
const FLESH_SAT_RANGE = [0.15, 0.55];
const FLESH_VAL_RANGE = [0.20, 0.85];

/**
 * Compute all three scores on an RGBA Float32 buffer.
 *
 * @param {Float32Array} rgba    width*height*4 linear ACEScg
 * @param {number} width
 * @param {number} height
 * @returns {{
 *   bandingScore: number,        // [0,1]
 *   haloScore: number,           // [0,1]
 *   plasticSkinScore: number,    // [0,1]
 *   aiLikelihoodScore: number,   // [0,1]
 *   detail: object               // raw intermediate metrics for debug/UI
 * }}
 */
export function computeAITellScores(rgba, width, height) {
    if (!(rgba instanceof Float32Array) || width < 16 || height < 16) {
        return zeroScores();
    }
    const lum = computeLuminance(rgba, width, height);

    const banding = scoreBanding(lum, width, height);
    const halo    = scoreEdgeHalo(rgba, lum, width, height);
    const plastic = scorePlasticSkin(rgba, lum, width, height);

    const ai = Math.max(banding.score, halo.score, plastic.score);

    return {
        bandingScore:      banding.score,
        haloScore:         halo.score,
        plasticSkinScore:  plastic.score,
        aiLikelihoodScore: ai,
        detail: {
            banding: banding.detail,
            halo:    halo.detail,
            plastic: plastic.detail,
        },
    };
}

function zeroScores() {
    return {
        bandingScore: 0, haloScore: 0, plasticSkinScore: 0,
        aiLikelihoodScore: 0, detail: {},
    };
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function computeLuminance(rgba, w, h) {
    const N = w * h;
    const lum = new Float32Array(N);
    for (let i = 0, p = 0; i < N; i++, p += 4) {
        lum[i] = 0.2126 * rgba[p] + 0.7152 * rgba[p + 1] + 0.0722 * rgba[p + 2];
    }
    return lum;
}

/* ── Score 1: Banding ────────────────────────────────────────────────── */

/* AI banding shows up as periodic "steps" in shadow histograms — instead
 * of a smooth distribution there are tall/empty alternating bins.
 * We compute a coefficient-of-variation on bin counts within the
 * shadow band. High CoV → suspicious banding pattern. */
function scoreBanding(lum, w, h) {
    const N = w * h;
    const NUM_BINS = 64;
    const hist = new Uint32Array(NUM_BINS);
    let shadowCount = 0;
    for (let i = 0; i < N; i++) {
        const v = lum[i];
        if (v < 0 || v > SHADOW_LUM_MAX) continue;
        const bin = Math.min(NUM_BINS - 1, Math.floor((v / SHADOW_LUM_MAX) * NUM_BINS));
        hist[bin] += 1;
        shadowCount += 1;
    }
    if (shadowCount < N * 0.005) {
        /* Less than 0.5% of pixels are in shadows — image isn't dark
         * enough to score banding. Return low score. */
        return { score: 0, detail: { shadowCount, reason: 'too-bright' } };
    }
    /* Compute normalised bin distribution. */
    const norm = new Float32Array(NUM_BINS);
    for (let b = 0; b < NUM_BINS; b++) norm[b] = hist[b] / shadowCount;
    /* Detect alternating-spike pattern: compare each bin to its neighbors. */
    let alt = 0;
    let smoothE = 0;
    for (let b = 1; b < NUM_BINS - 1; b++) {
        const expected = (norm[b - 1] + norm[b + 1]) * 0.5;
        const dev = Math.abs(norm[b] - expected);
        smoothE += expected;
        alt += dev;
    }
    /* Higher alt/smoothE ratio = more spikey = more bandy. */
    const ratio = smoothE > 0 ? alt / smoothE : 0;
    /* Empirically calibrate: real photos sit at ratio < 0.35,
     * AI banding pushes it past 0.50. Map to [0, 1]. */
    const score = clamp((ratio - 0.30) / 0.40);
    return { score, detail: { shadowCount, ratio: round3(ratio) } };
}

/* ── Score 2: Edge halo ──────────────────────────────────────────────── */

/* Real-photo edges of high luminance contrast typically DESATURATE
 * (because real lenses can't separate spectra perfectly under hard
 * contrast). AI-image edges OVERsaturate due to controlnet pixel-level
 * supervision biasing colour at boundaries.
 *
 * Measure: pearson correlation between |∇L| (per-pixel gradient magnitude
 * of luminance) and saturation. Real photos: r ≈ -0.05 to -0.20.
 * AI halos: r > 0.15. We map [0.15, 0.45] → score [0, 1]. */
function scoreEdgeHalo(rgba, lum, w, h) {
    const stride = 2;        /* skip every other pixel for speed */
    const xs = [], ys = [];
    let n = 0;
    for (let y = 1; y < h - 1; y += stride) {
        for (let x = 1; x < w - 1; x += stride) {
            /* |∇L| via Sobel-lite */
            const i = y * w + x;
            const gx = lum[i + 1] - lum[i - 1];
            const gy = lum[i + w] - lum[i - w];
            const grad = Math.sqrt(gx * gx + gy * gy);
            /* Saturation as max-min of RGB. */
            const p = i * 4;
            const r = rgba[p], g = rgba[p + 1], b = rgba[p + 2];
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const sat = max > 0 ? (max - min) / max : 0;
            xs.push(grad); ys.push(sat); n += 1;
        }
    }
    if (n < 64) return { score: 0, detail: { reason: 'too-small' } };
    const r = pearson(xs, ys);
    const score = clamp((r - 0.10) / 0.35);
    return { score, detail: { correlation: round3(r), samples: n } };
}

/* ── Score 3: Plastic skin ───────────────────────────────────────────── */

/* Detect flesh-tone regions via HSV hue/saturation/value heuristic
 * (no face model). Within those regions, compute the ratio of
 * mid-frequency energy (3×3 high-pass kernel) to total luminance.
 * Real skin has visible pore-scale detail → high ratio. AI skin is
 * smoothed out → low ratio. We INVERT so high score = plastic. */
function scorePlasticSkin(rgba, lum, w, h) {
    const N = w * h;
    let fleshCount = 0;
    let hpEnergy = 0;
    let lumSum = 0;
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x;
            const p = i * 4;
            const r = rgba[p], g = rgba[p + 1], b = rgba[p + 2];
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const v = max;
            const sat = v > 0 ? (max - min) / v : 0;
            if (v < FLESH_VAL_RANGE[0] || v > FLESH_VAL_RANGE[1]) continue;
            if (sat < FLESH_SAT_RANGE[0] || sat > FLESH_SAT_RANGE[1]) continue;
            const hue = computeHue(r, g, b, max, min);
            if (hue < FLESH_HUE_RANGE[0] || hue > FLESH_HUE_RANGE[1]) continue;
            /* High-pass: |8L - sum-of-8-neighbours|. */
            const c = lum[i];
            const sum8 = lum[i - 1] + lum[i + 1]
                       + lum[i - w] + lum[i + w]
                       + lum[i - w - 1] + lum[i - w + 1]
                       + lum[i + w - 1] + lum[i + w + 1];
            const hp = Math.abs(8 * c - sum8);
            hpEnergy += hp;
            lumSum += c;
            fleshCount += 1;
        }
    }
    if (fleshCount < N * 0.005) {
        return { score: 0, detail: { fleshCount, reason: 'no-skin' } };
    }
    const meanLum = lumSum / fleshCount;
    if (meanLum < 0.02) return { score: 0, detail: { fleshCount, reason: 'too-dark' } };
    const energyRatio = (hpEnergy / fleshCount) / meanLum;
    /* Empirical calibration on linear ACEScg buffers: real-skin
     * energyRatio sits around 0.04..0.12 (JPEG noise floor + pore
     * detail), AI plastic skin near 0.0..0.02. Map [0.04 - x] / 0.04
     * so score → 1 for energyRatio=0, → 0 by energyRatio=0.04. */
    const score = clamp((0.04 - energyRatio) / 0.04);
    return { score, detail: { fleshCount, energyRatio: round3(energyRatio) } };
}

function computeHue(r, g, b, max, min) {
    const d = max - min;
    if (d === 0) return 0;
    let h;
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * (((b - r) / d) + 2);
    else h = 60 * (((r - g) / d) + 4);
    if (h < 0) h += 360;
    return h;
}

/* ── Numeric helpers ─────────────────────────────────────────────────── */

function clamp(v) {
    if (!Number.isFinite(v)) return 0;
    if (v < 0) return 0;
    if (v > 1) return 1;
    return v;
}
function round3(v) { return Math.round(v * 1000) / 1000; }

function pearson(a, b) {
    const n = a.length;
    if (n === 0) return 0;
    let sa = 0, sb = 0;
    for (let i = 0; i < n; i++) { sa += a[i]; sb += b[i]; }
    const ma = sa / n, mb = sb / n;
    let num = 0, da = 0, db = 0;
    for (let i = 0; i < n; i++) {
        const xa = a[i] - ma, xb = b[i] - mb;
        num += xa * xb; da += xa * xa; db += xb * xb;
    }
    const denom = Math.sqrt(da * db);
    return denom > 0 ? num / denom : 0;
}
